import { useCallback, useEffect, useRef } from 'react';
import { buildAcFormThemeCss } from './acFormThemeCss';

/**
 * Re-themes the injected AC form(s) to the site's design tokens.
 *
 * Drops whitespace-only titles (some exports ship one, leaving a stray gap
 * above the first field), then appends an override stylesheet generated by
 * buildAcFormThemeCss() AFTER the form's own <style> blocks so its
 * declarations win the cascade (see acFormThemeCss.js for the specificity
 * strategy). This keeps the AC exports verbatim while the rendered form
 * follows the shipped brand palette — no timing-dependent inline styling.
 *
 * @param {HTMLDivElement} container - The DOM container holding the form
 */
function applyAcTheme(container) {
  container.querySelectorAll('._form-title').forEach((titleEl) => {
    if (!titleEl.textContent.trim()) {
      titleEl.remove();
    }
  });

  const formIds = Array.from(container.querySelectorAll('form._form'))
    .map((form) => form.id)
    .filter(Boolean);
  if (formIds.length > 0) {
    const themeStyle = document.createElement('style');
    themeStyle.textContent = formIds.map(buildAcFormThemeCss).join('\n');
    container.appendChild(themeStyle);
  }
}

/**
 * Data attribute used to mark the container as initialized in the DOM.
 * This is the primary guard against infinite re-initialization loops.
 * Unlike module-level state, DOM attributes survive HMR and are the
 * source of truth for whether scripts have already been injected.
 * @constant {string}
 */
const INITIALIZED_ATTR = 'data-ac-initialized';

/**
 * Checks whether the given HTML content appears to be the app's own SPA shell
 * (index.html) rather than actual ActiveCampaign form HTML.
 *
 * This can happen when a hosting provider (e.g., AWS Amplify) rewrites requests
 * for static files like /forms/19.html to index.html due to SPA catch-all redirect
 * rules. When the app's own bundle script is injected, it re-initializes React,
 * which re-mounts this component, causing an infinite loop.
 *
 * @param {string} html - The raw HTML string to check
 * @returns {boolean} True if the HTML looks like the app shell, false if it looks like a real form
 */
function isAppShellHtml(html) {
  return html.includes('<div id="root">') || html.includes('/static/js/main.');
}

/**
 * Filters out script elements that reference the app's own JavaScript bundle.
 * This is a defense-in-depth measure to prevent infinite re-initialization loops
 * in case the hosting provider serves the SPA index.html instead of the requested
 * static HTML file.
 *
 * @param {HTMLScriptElement} scriptEl - A script element to evaluate
 * @returns {boolean} True if the script is safe to inject, false if it should be filtered out
 */
function isSafeScript(scriptEl) {
  const src = scriptEl.getAttribute('src') || '';
  // Filter out the app's own bundle scripts (e.g., /static/js/main.*.js)
  if (src.includes('/static/js/main.') || src.includes('/static/js/bundle.')) {
    console.warn(
      '[ActiveCampaign] Blocked injection of app bundle script:',
      src
    );
    return false;
  }
  return true;
}

/**
 * ActiveCampaignEmbed component
 * Allows you to paste Active Campaign form code directly and it will execute properly.
 *
 * Styling: the AC export is injected verbatim, then an override stylesheet
 * generated by buildAcFormThemeCss() is appended after it, re-theming the form
 * to the site's design tokens (light card, Manrope, brand pill CTA).
 *
 * Guard strategy against infinite loops (defense in depth):
 * 1. DOM attribute on container (`data-ac-initialized`) - survives HMR and re-renders
 * 2. Ref-based `isInitialized` flag - survives re-renders within same mount
 * 3. DOM query for existing external scripts by src - prevents duplicate script tags
 *
 * The external AC script can trigger DOM mutations that cause React re-renders.
 * Without these guards, re-render -> useEffect -> add script -> DOM mutation ->
 * re-render creates an infinite loop.
 *
 * @param {Object} props - Component props
 * @param {string} props.htmlContent - The raw HTML content from Active Campaign (including scripts)
 * @param {boolean} [props.hideTitle] - Hide the export's built-in `._form-title`; set it when the page already renders its own heading above the embed
 * @returns {import('react').ReactElement} A div container for the Active Campaign form
 */
function ActiveCampaignEmbed({ htmlContent, hideTitle = false }) {
  const containerRef = useRef(null);
  /** @type {{current: boolean}} Ref-level guard against re-initialization within same mount */
  const isInitializedRef = useRef(false);

  /**
   * Restore non-script HTML content into the container (for remount scenarios
   * where the DOM element is empty but scripts are already loaded).
   * @type {function(HTMLDivElement, string): void}
   */
  const restoreHtmlContent = useCallback(
    (/** @type {HTMLDivElement} */ container, /** @type {string} */ html) => {
      if (container.hasChildNodes()) {
        return;
      }
      const wrapper = document.createElement('div');
      wrapper.innerHTML = html;
      while (wrapper.firstChild) {
        if (wrapper.firstChild.nodeName !== 'SCRIPT') {
          container.appendChild(wrapper.firstChild);
        } else {
          wrapper.removeChild(wrapper.firstChild);
        }
      }
      applyAcTheme(container);
    },
    []
  );

  useEffect(() => {
    if (!htmlContent || !containerRef.current) {
      return;
    }

    /** @type {HTMLDivElement} */
    const container = containerRef.current;

    // Guard 0: Detect if the HTML content is actually the app's own SPA shell.
    // This happens when the hosting provider (AWS Amplify) rewrites /forms/19.html
    // to index.html due to SPA catch-all redirect rules missing .html in the whitelist.
    // Injecting the app's own bundle script would re-initialize React and cause an
    // infinite mount -> inject -> re-init -> mount loop.
    if (isAppShellHtml(htmlContent)) {
      console.error(
        '[ActiveCampaign] Received app shell HTML instead of form content. ' +
          'Check hosting redirect rules — .html files may be rewritten to index.html.'
      );
      return;
    }

    // Guard 1: Check the DOM attribute (survives HMR, module reloads, and re-renders)
    if (container.getAttribute(INITIALIZED_ATTR) === 'true') {
      restoreHtmlContent(container, htmlContent);
      return;
    }

    // Guard 2: Check the ref (survives re-renders within the same mount cycle)
    if (isInitializedRef.current) {
      restoreHtmlContent(container, htmlContent);
      return;
    }

    // Guard 3: Check if external scripts from this form already exist in the DOM.
    // Parse htmlContent to find external script sources and check if any are already loaded.
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;
    const scriptElements = tempDiv.querySelectorAll('script[src]');
    const hasExistingScripts = Array.from(scriptElements).some((script) =>
      document.querySelector(`script[src="${script.getAttribute('src')}"]`)
    );

    if (hasExistingScripts) {
      // Scripts already in the DOM -- mark as initialized and restore HTML only
      container.setAttribute(INITIALIZED_ATTR, 'true');
      isInitializedRef.current = true;
      restoreHtmlContent(container, htmlContent);
      return;
    }

    // Mark as initialized using all guard mechanisms BEFORE injecting scripts
    container.setAttribute(INITIALIZED_ATTR, 'true');
    isInitializedRef.current = true;

    console.log('[ActiveCampaign] Initializing form content');

    // Clear container for fresh initialization
    container.innerHTML = '';

    // Create a wrapper div to hold all content
    const wrapper = document.createElement('div');
    wrapper.innerHTML = htmlContent;

    // Extract scripts before moving DOM nodes
    const scripts = wrapper.querySelectorAll('script');

    // Append non-script content first
    while (wrapper.firstChild) {
      if (wrapper.firstChild.nodeName !== 'SCRIPT') {
        container.appendChild(wrapper.firstChild);
      } else {
        wrapper.removeChild(wrapper.firstChild);
      }
    }

    // Re-theme the injected form(s) to the site's design tokens before the AC
    // scripts run; the override stylesheet lands after the export's own
    // <style> blocks, so it applies declaratively with no timing dependency.
    applyAcTheme(container);

    // Then handle scripts one by one
    scripts.forEach((oldScript) => {
      // Defense-in-depth: skip any script that references the app's own bundle.
      // This prevents infinite loops if the HTML somehow contains app bundle scripts.
      if (!isSafeScript(oldScript)) {
        return;
      }

      const newScript = document.createElement('script');

      // Copy attributes
      Array.from(oldScript.attributes).forEach((attr) => {
        newScript.setAttribute(attr.name, attr.value);
      });

      // Copy content
      if (oldScript.textContent) {
        newScript.textContent = oldScript.textContent;
      }

      // For external scripts (has src attribute), do a final DOM check before adding
      if (oldScript.src) {
        const existingScript = document.querySelector(
          `script[src="${oldScript.src}"]`
        );
        if (existingScript) {
          console.log(
            '[ActiveCampaign] Script already exists, skipping:',
            oldScript.src
          );
          return; // Skip - script already in DOM
        }
        // Add external scripts to document.body (so they persist across remounts)
        document.body.appendChild(newScript);
        console.log(
          '[ActiveCampaign] Added external script to document.body:',
          oldScript.src
        );
      } else {
        // Add inline scripts to container (they need to be in context)
        container.appendChild(newScript);
        console.log('[ActiveCampaign] Added inline script to container');
      }
    });

    // Note: we intentionally do NOT remove the DOM initialized attribute
    // or external scripts on unmount, as they should persist across remounts
  }, [htmlContent, restoreHtmlContent]);

  return (
    <div
      ref={containerRef}
      className={hideTitle ? 'ac-embed--hide-title' : undefined}
    />
  );
}

export default ActiveCampaignEmbed;
